<template>
  <div class="wrapper">
    <dashboard-bar refreshButton />
    <div class="bg-white p-4">
      <div class="col-12 p-0 mb-3">
        <div class="header">
          <span class="header-icon">
            <i class="fas fa-clock"></i>
          </span>
          <h2>{{ $t('reminders.pageTitle') }}</h2>
          <span
            class="ml-3 refresh-icon"
            :title="$t('reminders.updateList')"
            @click="getReminders"
          >
            <font-awesome-icon :icon="['fas', 'sync-alt']" />
          </span>
        </div>
      </div>

      <div class="row mt-2 pl-3 filter-type"><strong>Pesquisar por</strong></div>
      <div class="sub-header">
        <div class="filters">
          <div class="filter-type">
            <b-form-select
              v-model="filterType"
              @change="setFilterType"
              :options="filterTypeOptions"
            ></b-form-select>
          </div>

          <div class="reminders-search-field">
            <input
              type="text"
              class="form-control"
              id="input-answer"
              :placeholder="$t('reminders.searchInput')"
              v-model="filter"
              @keyup="searchByFilter"
            />
            <button @click="cleanFilter" class="btn btn-outline-primary">
              {{ $t('reminders.searchButton') }}
            </button>
          </div>

          <b-form-select
            id="channel"
            v-model="selectedChannel"
            :options="optionChannel"
          ></b-form-select>
        </div>

        <div class="select-all-area">
          <div
            v-if="this.userProfile.permissions.submits.cancelReminder"
            class="custom-switch"
          >
            <input
              type="checkbox"
              class="custom-control-input"
              id="switch-select-resend"
              v-model="pageSelected"
              :disabled="allRemindersSelected"
              @click="selectPage($event)"
            />
            <label class="custom-control-label" for="switch-select-resend">
              {{ $t('reminders.selectPage') }}
            </label>
          </div>
        </div>
      </div>

      <transition name="fade">
        <div
          v-if="selectedIds.length > 0 || allRemindersSelected"
          class="select-all-area mb-3"
        >
          <div v-if="!allRemindersSelected" class="select-all-description">
            {{
              $t('reminders.selectInfo.remindersLength', {
                length: selectedIds.length,
              })
            }}

            <span @click="selectAllReminders" class="link idx-bold">
              {{
                $t('reminders.selectInfo.selectAll', {
                  length: total,
                })
              }}
            </span>
          </div>

          <div v-else class="select-all-description">
            {{
              $t('reminders.selectInfo.allReminders', {
                length: total,
              })
            }}

            <span @click="cancelAllReminders" class="link idx-bold">
              {{ $t('reminders.selectInfo.cleanSelection') }}
            </span>
          </div>
        </div>
      </transition>

      <b-table striped :items="items" :fields="fields" :busy="loading">
        <template v-slot:table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner type="grow" class="align-middle"></b-spinner>
          </div>
        </template>

        <template v-slot:cell(client)="row">
          <span class="user-info">
            {{ checkName(row.item.anonymousResponse, row.item.inviteId.name) }}
          </span>
          <br />
          <span class="user-info">
            E-mail:
            {{
              checkUserData(
                row.item.inviteId.email,
                userProfile.permissions.answers.anonymous_profile,
              )
            }}
          </span>
          <br />
          <span class="user-info">
            Telefone:
            {{
              checkUserData(
                row.item.inviteId.phone,
                userProfile.permissions.answers.anonymous_profile,
              )
            }}
          </span>
        </template>

        <template
          v-if="this.userProfile.permissions.submits.cancelReminder"
          v-slot:cell(cancel)="row"
        >
          <b-form-checkbox
            class="ml-3"
            :checked="verifyChecked(row.item._id) || allRemindersSelected"
            @change="cancelReminder(row.item._id, $event)"
            :disabled="allRemindersSelected"
          >
          </b-form-checkbox>
        </template>
      </b-table>

      <div class="footer">
        <div class="pagination-area-reminders mt-1">
          <paginate
            v-model="page"
            :pageCount="Math.ceil(total / limit)"
            :prevText="'‹'"
            :nextText="'›'"
            :activeClass="'active'"
            :containerClass="'pagination b-pagination'"
            :pageClass="'page-item'"
          ></paginate>
        </div>

        <div class="buttons-area">
          <button type="button" @click="download('csv')" class="export-button">
            <i class="fas fa-file-csv" />
          </button>

          <button type="button" @click="download('xlsx')" class="export-button">
            <i class="fas fa-file-excel" />
          </button>

          <button
            v-if="this.userProfile.permissions.submits.cancelReminder"
            type="button"
            class="btn btn-outline-primary cancel-button"
            @click="sendCancellation"
          >
            {{ $t('reminders.cancelRemindersButton') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Paginate from 'vuejs-paginate';
import { format } from 'date-fns';
import swal from 'sweetalert';
import dashboardBar from '@/components/molecules/dashboard-bar';
import api from '../../../services/api';

export default {
  components: { Paginate, dashboardBar },

  computed: {
    ...mapState('actions', ['myActionId', 'actionGroupData', 'myActionsIds']),
    ...mapState('branches', ['myIh1', 'myIh2', 'myHierarchies1', 'myHierarchies2']),
    ...mapState('authentication', ['userProfile']),
    ...mapState('calendar', ['calendarDates']),
    ...mapState('companies', ['myMetric']),
    ...mapState('indicators', ['indicatorName', 'indicatorData']),
  },

  data: () => ({
    selectedChannel: null,
    optionChannel: [],
    loading: false,
    userAuth: null,
    total: 0,
    page: 1,
    limit: 10,
    filter: null,
    fields: [],
    items: [],
    selectedIds: [],
    allRemindersSelected: false,
    pageSelected: false,
    filterTypeOptions: [
      {
        text: "ControlId",
        value: 'controlId',
      },
      {
        text: "Email",
        value: 'email',
      },
      {
        text: "Nome",
        value: 'name',
      },
      {
        text: "Telefone",
        value: 'phone',
      },
    ],
    filterType: 'controlId',
  }),

  watch: {
    selectedChannel: function watchSelectedChannel() {
      this.getReminders();
    },

    'actionGroupData._id': function watchGroupId() {
      this.getReminders();
    },

    myActionId: function watchActionId() {
      this.getReminders();
    },

    myActionsIds: function watchMyActionsIds(){
      this.getReminders();
    },

    myHierarchies1: function watchMyHierarchies1() {
      this.getReminders();
    },

    myIh1: function watchIh1() {
      this.getReminders();
    },

    myIh2: function watchIh2() {
      this.getReminders();
    },

    myHierarchies2: function watchMyHierarchies2() {
      this.getReminders();
    },

    'calendarDates.end': function watchCalendarDates() {
      this.getReminders();
    },

    myMetric: function watchMetric() {
      this.getReminders();
    },

    indicatorData: function watchIndicatorData() {
      this.getReminders();
    },

    page: function watchPage() {
      this.getReminders();
    },
  },

  methods: {
    ...mapActions('calendar', ['setDefaultDates']),

    checkName(anonymous, name) {
      if (
        anonymous &&
        !this.userProfile.permissions.answers.anonymous_profile
      ) {
        return 'Anônimo';
      } else if (this.userProfile.permissions.answers.anonymous_profile) {
        return 'Nome: ***************';
      } else if (name) {
        return name;
      }
      return '-';
    },

    checkUserData(value, profile) {
      if (value === 'empty') {
        return '-';
      } else if (profile) {
        return '************';
      } else if (value) {
        return value;
      }
      return '-';
    },

    async getReminders() {
      this.loading = true;

      this.pageSelected = false;

      const response = await api.get(`/reminders`, {
        headers: {
          'x-access-token': this.userAuth.token,
        },
        params: {
          page: this.page,
          limit: this.limit,
          filter: this.filter,
          channel: this.selectedChannel,
          groupId: this.actionGroupData._id,
          actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
          startDate: this.calendarDates.start ? this.calendarDates.start : null,
          endDate: this.calendarDates.end ? this.calendarDates.end : null,
          ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
          ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
          metric: this.myMetric,
          indicator: this.indicatorName,
          indicatorValue: this.indicatorData.value,
          indicatorRange: this.indicatorData.range,
          filterType: this.filterType,
        },
      });

      this.items = response.data.reminders;
      this.total = response.data.total;

      this.items.forEach((el) => {
        el.date = this.dateFormat(el.date);
      });

      this.loading = false;
    },

    async sendCancellation() {
      if (this.selectedIds.length <= 0 && !this.allRemindersSelected) {
        swal({
          title: this.$t('reminders.messages.sendNoReminder.title'),
          text: this.$t('reminders.messages.sendNoReminder.description'),
          icon: 'warning',
        });
      } else {
        swal({
          title: this.$t('reminders.messages.send.title'),
          text: this.$t('reminders.messages.send.description'),
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        }).then(async (answer) => {
          if (answer) {
            try {
              await api.delete(`/reminders`, {
                data: {
                  all: this.allRemindersSelected,
                  ids: this.selectedIds.length > 0 ? this.selectedIds : null,
                },
                headers: {
                  'x-access-token': this.userAuth.token,
                },
                params: {
                  channel: this.selectedChannel,
                  groupId: this.actionGroupData._id,
                  actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
                  startDate: this.calendarDates.start
                    ? this.calendarDates.start
                    : null,
                  endDate: this.calendarDates.end
                    ? this.calendarDates.end
                    : null,
                  ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
                  ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
                  metric: this.myMetric,
                  indicator: this.indicatorName,
                  indicatorValue: this.indicatorData.value,
                  indicatorRange: this.indicatorData.range,
                  filterType: this.filterType,
                },
              });

              this.selectedIds = [];
              this.allRemindersSelected = false;
              this.pageSelected = false;

              this.getReminders();
            } catch (error) {
              swal(
                this.$t('reminders.messages.sendError.title'),
                'error',
                this.$t('reminders.messages.sendError.description'),
              );
            }
          }
        });
      }
    },

    async searchByFilter() {
      if (this.filter !== '' && this.filter !== null) {
        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(this.getReminders, 600);
      }

      if (this.filter === '') {
        this.filter = null;
        this.getReminders();
      }
    },

    async download(extension) {
      // checks if the total docs to download is greater then 100k
      if (this.total > 100000) {
        swal({
          title: this.$t('scheduling.messages.maxDownloadError.title'),
          text: this.$t('scheduling.messages.maxDownloadError.description'),
          icon: 'warning',
        });
        return;
      }
      try {
        await api.get(`/reminders/download/${extension}`, {
          headers: {
            'x-access-token': this.userAuth.token,
          },
          params: {
            filter: this.filter,
            channel: this.selectedChannel,
            groupId: this.actionGroupData._id,
            actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
            startDate: this.calendarDates.start
              ? this.calendarDates.start
              : null,
            endDate: this.calendarDates.end ? this.calendarDates.end : null,
            ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
            ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
            metric: this.myMetric,
            indicator: this.indicatorName,
            indicatorValue: this.indicatorData.value,
            indicatorRange: this.indicatorData.range,
            filterType: this.filterType,
          },
        });

        swal({
          title: this.$t('reminders.messages.download.title'),
          text: this.$t('reminders.messages.download.description'),
          icon: 'success',
        });
      } catch (error) {
        swal({
          title: this.$t('reminders.messages.downloadError.title'),
          text: error.response.data.message,
          icon: 'error',
        });
      }
    },

    cleanFilter() {
      if (this.filter) {
        this.filter = null;

        this.getReminders();
      }
    },

    dateFormat(date) {
      if (date) {
        return format(new Date(date), 'dd/MM/yyyy HH:mm');
      }

      return '-';
    },

    cancelReminder(reminderId, checked) {
      if (checked) {
        this.selectedIds.push(reminderId);
      } else {
        this.selectedIds = this.selectedIds.filter((id) => id !== reminderId);
      }
    },

    selectAllReminders() {
      this.allRemindersSelected = true;

      this.pageSelected = true;

      this.selectedIds = [];
    },

    cancelAllReminders() {
      this.allRemindersSelected = false;

      this.pageSelected = false;
    },

    verifyChecked(reminderId) {
      const idFound = this.selectedIds.indexOf(reminderId);

      this.verifyPageSelected();

      if (idFound >= 0) {
        return true;
      }

      return false;
    },

    selectPage(event) {
      if (event.target.checked) {
        this.items.forEach((reminder) => {
          const idFound = this.selectedIds.indexOf(reminder._id);

          if (idFound < 0) {
            this.selectedIds.push(reminder._id);
          }
        });
      } else {
        this.items.forEach((reminder) => {
          const idFound = this.selectedIds.indexOf(reminder._id);

          if (idFound >= 0) {
            this.selectedIds.splice(idFound, 1);
          }
        });
      }
    },

    verifyPageSelected() {
      if (this.allRemindersSelected) {
        this.pageSelected = true;
      } else {
        const ids = this.items.filter((reminder) =>
          this.selectedIds.includes(reminder._id),
        );

        if (ids.length >= this.items.length) {
          this.pageSelected = true;
        } else {
          this.pageSelected = false;
        }
      }
    },

    setFilterType(value) {
      this.filterType = value;
    },
  },

  created() {
    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));

    this.filterTypeOptions = [
      {
        text: this.$t('answers.filterTypeOptions.id'),
        value: 'controlId',
      },
      {
        text: this.$t('answers.filterTypeOptions.email'),
        value: 'email',
      },
      {
        text: this.$t('answers.filterTypeOptions.name'),
        value: 'name',
      },
      {
        text: this.$t('answers.filterTypeOptions.phone'),
        value: 'phone',
      },
    ];

    this.fields = [
      {
        key: 'controlId',
        label: this.$t('reminders.remindersTable.labels.id'),
      },
      {
        key: 'actionId.name',
        label: this.$t('reminders.remindersTable.labels.action'),
      },
      {
        key: 'client',
        label: this.$t('reminders.remindersTable.labels.client'),
      },
      { key: 'date', label: this.$t('reminders.remindersTable.labels.date') },
      {
        key: 'actionId.channel',
        label: this.$t('reminders.remindersTable.labels.channel'),
      },
      {
        key: 'cancel',
        class: 'text-center',
        label: this.$t('reminders.remindersTable.labels.cancel'),
      },
    ];

    this.optionChannel = [
      { value: null, text: this.$t('reminders.channelFilter.select') },
      { value: 'email', text: this.$t('reminders.channelFilter.email') },
      { value: 'sms', text: this.$t('reminders.channelFilter.sms') },
      { value: 'emailsms', text: this.$t('reminders.channelFilter.all') },
    ];

    // this.setDefaultDates();

    this.getReminders();
  },
};
</script>

<style lang="scss" scoped>
@import '@/themes/default/variables.scss';
@import '@/themes/default/placeholders.scss';

.wrapper {
  padding: 8px;
}

h2 {
  text-transform: uppercase;
  font-size: 21px;
  font-weight: 100;
  color: $gray;
}

.header {
  display: flex;
  flex-direction: row;

  .header-icon {
    float: left;
    font-size: 12px;
    margin-top: 4px;
    padding-right: 12px;
    color: $gray;
  }

  .refresh-icon {
    margin-top: 1px;
    color: $primary;
    cursor: pointer;
    &:hover {
      color: green;
    }
  }
}

.sub-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;

  .filters {
    display: flex;
    flex-direction: row;
    gap: 5px;

    .reminders-search-field {
      display: flex;
      flex-direction: row;
      min-width: 300px;
      margin-right: 12px;

      input {
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
      }

      button {
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
      }
    }
  }
}

.select-all-area {
  display: flex;
  justify-content: center;
  align-items: center;

  .select-all-description {
    padding: 10px 30px;
    color: #5f6368;
    background-color: rgba(241, 243, 244, 0.6);
  }

  .link {
    color: #1a73e8;
    cursor: pointer;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.user-info {
  color: rgb(133, 131, 131);
}

.pagination-area-reminders >>> a {
  position: relative;
  display: block;
  padding: 0.5rem 0.75rem;
  margin-left: -1px;
  line-height: 1.25;
  color: #4ce595;
  background-color: #fff;
  border: 1px solid #dee2e6;
}

.footer {
  display: flex;
  justify-content: space-between;

  .cancel-button {
    max-height: 40px;
    margin-bottom: 16px;
  }

  .export-button {
    color: $primary;
    font-size: 34px;
    border: none;
    background: none;
    margin: 0;
    padding: 0;
    margin-right: 8px;
    cursor: pointer;
  }
}

.filter-type {
  display: flex;
  flex-direction: row;
  max-width: 350px;
  margin-right: 10px;
  width: 350px;
}
</style>

<style>
.pagination-area-reminders .active a {
  text-decoration: none;
  z-index: 1;
  color: #fff;
  background-color: #4ce595;
  border-color: #4ce595;
}

.pagination-area-reminders a {
  outline: none;
}
</style>



// WEBPACK FOOTER //
// src/components/organisms/reminders/index.vue